import { useCallback, useMemo } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';

import { ResolvedThemeMode, ThemeMode } from '@/theme/types';
import { useRecoilState } from 'recoil';

import { themeModeState } from './themeModeState';

type UseThemeModeReturn = {
  mode: ThemeMode;
  resolvedMode: ResolvedThemeMode;
  prefersDarkMode: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
};

function resolveThemeMode(mode: ThemeMode, prefersDarkMode: boolean): ResolvedThemeMode {
  if (mode === ThemeMode.System) {
    return prefersDarkMode ? ResolvedThemeMode.Dark : ResolvedThemeMode.Light;
  }

  return mode === ThemeMode.Dark ? ResolvedThemeMode.Dark : ResolvedThemeMode.Light;
}

const useThemeMode = (): UseThemeModeReturn => {
  const [mode, setMode] = useRecoilState(themeModeState);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });

  const resolvedMode = useMemo(
    () => resolveThemeMode(mode, prefersDarkMode),
    [mode, prefersDarkMode],
  );

  const toggleTheme = useCallback(() => {
    setMode((prev) => {
      const prevResolved = resolveThemeMode(prev, prefersDarkMode);
      return prevResolved === ResolvedThemeMode.Dark ? ThemeMode.Light : ThemeMode.Dark;
    });
  }, [prefersDarkMode, setMode]);

  return {
    mode,
    resolvedMode,
    prefersDarkMode,
    setThemeMode: setMode,
    toggleTheme,
  };
};

export default useThemeMode;
export type { UseThemeModeReturn };
